<script lang="ts" setup>
import isEmailValidator from 'validator/es/lib/isEmail'
import { InputPassword, InputText, InputAggree } from '#components'

const t2 = useT2({
  'Bad values': 'Neteisingos reikšmės',
  'Bad email': 'Neteisingas el. paštas',
  'Bad code': 'Neteisingas kodas',
  'Bad form': 'Netinkama forma',
  'Bad password': 'Neteisingas slaptažodis',
  Email: 'El. paštas',
  Logout: 'Atsijungti',
  Cancel: 'Atšaukti',
  Continue: 'Tęsti',
  'Sign in': 'Prisijungti',
  'Get one-time sign in code to email': 'Gauti vienkartinį prisijungimo kodą el. paštu',
  'Forgot password': 'Pamiršote slaptažodį',
  'Get password reset code': 'Gauti slaptažodžio atkūrimo kodą',
  'New password': 'Naujas slaptažodis',
  'Update password': 'Atnaujinti slaptažodį',
  'Confirm email': 'Patvirtinti el. paštą',
  'Get email confirmation code': 'Gauti el. pašto patvirtinimo kodą',
  whyEmailCode: {
    en: "Email confirmation is important to verify that you entered your email correctly. We all make mistakes. Sometimes emails are misspelled and our customers can't get important emails about their orders.",
    lt: 'El. pašto patvirtinimas yra svarbus įsitikinti, kad įvedėte savo el. paštą teisingai. Visi darome klaidų. Kartais el. pašto adresai yra neteisingai parašyti ir mūsų klientai negali gauti svarbių laiškų apie savo užsakymus.',
  },
  'Edit profile': 'Redaguoti profilį',
  Register: 'Registruotis',
  'Manage account': 'Tvarkyti paskyrą',
})

const props = defineProps<{
  fixedEmail?: string | None
  onAuth?: () => void
  glow?: boolean
  checkout?: gen.InputBuyerAuthCheckout | None
  delay?: number
}>()

const ads = useAds()

const { myLink } = useLinks()

const elEmail = useTemplateRef('email')
const elPassword = useTemplateRef('password')
const elSuccess = useTemplateRef('elemSuccess')
const formSuccess = () => nextTick(() => elSuccess?.value?.click())

const notify = useNotify()
const key = useId()
const { data, reset } = useAuthLoginUltimate()

const ui = shallowReactive({
  ready: !props.delay,
  focused: false,
  passwordVisible: false,
})

const timer3 = useTimeout()
onNuxtReady(() => props.delay && timer3.set(() => !ui.ready && (ui.ready = true), props.delay ?? 0))

const isEmailValid = (e: string): boolean => !!e && isEmailValidator(e)
const emailInput = computed({
  get: () => props.fixedEmail || data.emailHolder || '',
  set: (i: string) => (data.emailHolder = i),
})
const uiEmailExists = computed((): boolean => !!emailInput.value.length)
const uiEmailValid = computed((): boolean => isEmailValid(emailInput.value))
const pending = computed((): boolean => props.glow || !helpers.isIn(data.state, 'is auth', 'enter email') || uiEmailExists.value)
const idKey = computed(() => `auth:${key}${data.rerenderKey}`)
const user = computed(() => data.user)

const timer2 = useTimeout()
function focusInput(state: AuthUltimateStateType) {
  const focusMap: Partial<Record<AuthUltimateStateType, () => VueComponent | None>> = {
    'enter email': () => elEmail?.value,
    'enter password': () => elPassword?.value,
  }
  timer2.set(() => helpers.getHTMLElement(focusMap[state]?.())?.focus(), 10)
}

function setState(state: AuthUltimateStateType): NotVoid {
  data.state = state
  focusInput(state)
  return null
}

watch(
  () => data.state,
  (state, prev) => {
    if (state !== 'enter password') ui.passwordVisible = false
    if (state === 'is auth' && prev !== 'is auth') props.onAuth?.()
  },
  { immediate: true }
)

function resetInputs(state: AuthUltimateStateType, options?: { simple?: boolean }) {
  ui.passwordVisible = false
  reset(state, options)
  if (!options?.simple) focusInput(state)
}

type InputBuyerAuth = Omit<gen.InputBuyerAuth, '_checkout' | 'widget'>

function getCheckoutInput(input: InputBuyerAuth): gen.InputBuyerAuthCheckout | None {
  const checkout = props.checkout
  if (!checkout) return

  const isCheckoutDataOn: Record<keyof InputBuyerAuth, boolean> = {
    login: true,
    loginCodeUse: true,
    register: true,
    logout: true,
    forgotPasswordUseCode: true,
    registerCodeGet: true,
    loginCodeGet: true,
    loginFacebook: true,
    loginGoogle: true,
    authCodeResend: false,
    emailStatus: false,
    authEmailValid: false,
    forgotPasswordGetCode: false,
    verifyUrlCode: false,
    oauthGoogle: true,
  }

  const keys = Object.keys(input) as (keyof InputBuyerAuth)[]
  return keys.some((i) => isCheckoutDataOn[i]) ? checkout : undefined
}

function mutate(input: InputBuyerAuth, cb?: (i: NN<gen.BuyerAuthMutation>['buyerAuth']) => unknown) {
  data.loading = true

  for (const key in input) {
    if (helpers.isDef(input[key as keyof typeof input])) {
      data.mutation = key as keyof typeof input
      break
    }
  }

  return notify
    .watch(
      useBuyerAuthUpdate({ ...input, _checkout: getCheckoutInput(input), widget: 'ultimate2' }).then((i) => {
        const res = i?.data?.buyerAuth
        if (res) cb?.(res)
        return i
      })
    )
    .finally(() => {
      data.loading = false
      data.mutation = undefined
    })
}

export type MutateFunc = typeof mutate

function logout() {
  return mutate({ logout: { value: true } })
}

function login(): NotVoid {
  const values: gen.InputBuyerAuth['login'] = { email: emailInput.value, password: data.password }
  if (!values?.email || !values?.password) return notify.error(t2('Bad values'))
  return mutate({ login: values }, (i) => {
    if (i.responseInfo.success) {
      if (i.me?.user?.doc.email) {
        ads.save?.(() => ({ event: 'login', user_id: i.me?._id, email: i.me?.user?.doc.email }))
      }
      formSuccess()
    }
    return i
  })
}

// check if email exists and the status of email
function emailEntered() {
  const email = emailInput.value
  if (!isEmailValid(email)) return notify.error(t2('Bad email'))
  data.emailHolder = email
  return mutate({ emailStatus: { email } }, (res) => {
    data.emailInvalid = res.emailInvalid === true
    setState(res.emailStatus ? 'enter password' : 'register form')
    return res
  })
}

let fixedEmailInitialized = false

watch(
  () => [user.value.userId && !user.value.isAuth ? props.fixedEmail : undefined, data.mutation, data.emailHolder] as const,
  ([email, mutation, emailHolder]) => {
    if (!email?.length) return

    if (emailHolder === email) {
      if (data.state !== 'enter email') {
        fixedEmailInitialized = true
        return
      }

      if (!fixedEmailInitialized && !mutation) {
        fixedEmailInitialized = true
        emailEntered()
      }
      return
    }

    if (mutation) return

    fixedEmailInitialized = true
    reset('enter email')
    data.emailHolder = email
    emailEntered()
  },
  { immediate: true }
)

// check if email exists and the status of email
function loginWithCodeGetCode() {
  const email = emailInput.value
  if (!isEmailValid(email)) return notify.error(t2('Bad email'))
  return mutate({ loginCodeGet: { email, fullurl: window.location.href } }, (res) => {
    if (!res?.responseInfo?.success) return
    data.loginRef = res?.codeRefNumber ?? undefined
    data.state = 'verify email loading'
    return res
  })
}

function sendCodeStep2(codeFromEmail: string | None) {
  if (!codeFromEmail) return notify.error(t2('Bad code'))
  const email = emailInput.value
  if (!isEmailValid(email)) return notify.error(t2('Bad email'))
  return mutate({ loginCodeUse: { email, codeFromEmail } })
}

const formRegiser = {
  refs: useRefs({
    name: InputText,
    lastname: InputText,
    password: InputPassword,
    newsletter: InputAggree,
  }),
  async submit() {
    const values = getFormValue(this.refs)
    const email = emailInput.value
    if (!isEmailValid(email)) return notify.error(t2('Bad email'))
    const register: gen.InputBuyerAuthRegisterValues = {
      email,
      name: values.name,
      lastname: values.lastname,
      password: values.password,
    }

    const emailingSettings: gen.InputSubscriptionsEmailingSettings = { sellerId: RETAIL_SELLER_ID, newsletter: values.newsletter }

    // if (data.state === 'register form') {
    const res = (await mutate({ registerCodeGet: { values: register, fullurl: window.location.href } }))?.data?.buyerAuth
    if (!res?.responseInfo.success) return null
    data.registerRef = res?.codeRefNumber ?? undefined
    // }

    data.register = { register, emailingSettings }
    data.state = 'register confirm'
    return null
  },
}

function registerConfirm(codeFromEmail: string | None) {
  if (!codeFromEmail) return notify.error(t2('Bad code'))
  const register = data.register?.register
  const emailingSettings = data.register?.emailingSettings
  if (!register) return notify.error(t2('Bad form'))
  const password = register?.password
  if (!password) return notify.error(t2('Bad password'))
  const email = emailInput.value
  if (!isEmailValid(email)) return notify.error(t2('Bad email'))
  return mutate({ register: { codeFromEmail, email: register.email } }, (i) => {
    if (i.responseInfo.success) {
      formSuccess()
      const me = i.me
      if (me?.user?.doc.email) ads.save?.(() => ({ event: 'sign_up', email: me.user?.doc.email, newsletter: false }))
      if (emailingSettings?.sellerId) useBuyerSubscriptionsMutation({ set: emailingSettings }).catch((err) => console.error('checkout:useBuyerSubscriptionsMutation', err))
      return i
    }
  })
}

const formForgot = {
  refs: useRefs({
    password: InputPassword,
  }),
  submit() {
    const email = emailInput.value
    const values = getFormValue(this.refs)
    if (!isEmailValid(email)) return { error: t2('Bad email') }
    return mutate({ forgotPasswordGetCode: { email, fullurl: window.location.href } }, (res) => {
      if (!res.responseInfo.success) return
      data.forgotRef = res.codeRefNumber ?? undefined
      data.state = 'forgot password confirm'
      data.forgotNewPassword = values.password
      return res
    })
  },
}

function forgotPassword2(codeFromEmail: string | None) {
  if (!codeFromEmail) return notify.error(t2('Bad code'))
  const passwordNew = data.forgotNewPassword
  if (!passwordNew) return notify.error(t2('Bad password'))
  const email = emailInput.value
  if (!isEmailValid(email)) return notify.error(t2('Bad email'))
  return mutate({ forgotPasswordUseCode: { codeFromEmail, passwordNew, email } }, (i) => {
    if (i.responseInfo.success) formSuccess()
    return i
  })
}
</script>

<template>
  <div :key="data.rerenderKey" :ref="notify.elem" :class="[$style.cont, { pending }]">
    <form v-if="ui.ready" :id="idKey" autocomplete="off" @submit.prevent>
      <button ref="elemSuccess" :form="idKey" type="submit" :class="$style.btnSucess" />

      <div :class="[$style.b1, data.emailInvalid ? '_bRed _br14 _hidden' : '_b5 _bw001']">
        <span :class="$style.label">{{ t2('Email') }}<UiMsg2 :err="data.emailInvalid ? t2('Bad email') : undefined" /></span>
        <span v-if="user?.isAuth" class="_fw-l _pt08 _pl14">{{ user.email }}</span>
        <span v-else-if="props.fixedEmail" class="_fw-l _pt08 _pl14">{{ props.fixedEmail }}</span>
        <input
          v-else
          ref="email"
          v-model="emailInput"
          :class="$style.input"
          required="true"
          :disabled="data.loading"
          autocomplete="username"
          can-field="accountName"
          autocorrect="off"
          autocapitalize="none"
          aria-required="true"
          autofocus="true"
          name="username"
          type="email"
          aria-invalid="false"
          :placeholder="t2('Email')"
          @keypress.enter.prevent="() => emailEntered()"
        />

        <ButtonIcon
          v-if="(uiEmailExists || user.isAuth) && !fixedEmail"
          :class="$style.iconMain"
          :loading="data.mutation === 'logout' && data.loading"
          size="36"
          :txt="data.state === 'is auth' ? t2('Logout') : t2('Cancel')"
          @click.passive="() => (data.state === 'is auth' ? logout() : resetInputs('enter email'))"
        >
          <IconLogout v-if="data.state === 'is auth'" class="_svg18" />
        </ButtonIcon>
      </div>

      <UiGrow :open="data.state === 'enter email' && uiEmailValid">
        <div :class="$style.boxPad">
          <ButtonActionArrow color :txt="t2('Continue')" :submit="() => emailEntered()" />
        </div>
      </UiGrow>

      <UiGrow :open="data.state === 'enter password'" :render-on-close="data.state === 'enter email'">
        <div :class="$style.b1">
          <span :class="$style.label">{{ t2('Password') }}</span>
          <input
            ref="password"
            v-model="data.password"
            :class="$style.input"
            :disabled="data.loading"
            :type="ui.passwordVisible ? 'text' : 'password'"
            placeholder="••••••••••••••••"
            autocomplete="current-password"
            name="password"
            aria-required="true"
            required="true"
            can-field="password"
            aria-invalid="false"
            @keypress.enter.prevent="() => login()"
          />
          <ButtonIcon :class="$style.iconMain" size="36" :txt="ui.passwordVisible ? t2('Hide') : t2('Show')" @click.passive="() => (ui.passwordVisible = !ui.passwordVisible)">
            <IconEye :closed="!ui.passwordVisible" :class="$style.iconSettings" />
          </ButtonIcon>
        </div>

        <div :class="$style.boxPad">
          <ButtonActionArrow color :txt="t2('Sign in')" :submit="login" />
          <div :class="$style.boxRight">
            <ButtonAction :txt="t2('Get one-time sign in code to email')" :submit="loginWithCodeGetCode" small />
            <ButtonAction :txt="t2('Forgot password')" :submit="() => setState('forgot password')" small />
          </div>
        </div>
      </UiGrow>

      <UiGrow :open="data.state === 'forgot password'" :render-on-close="data.state === 'forgot password confirm'">
        <MyForm :form="formForgot" :txt="t2('Get password reset code')" class="_hm50 _pr04 _pl04 _pt1 _pb1">
          <InputPassword :ref="formForgot.refs.password" type="password:register" :label="t2('New password')" />

          <template #button="{ submit, label }">
            <div class="_row _gap1">
              <ButtonAction color :txt="ts(label)" :submit="submit" />

              <ButtonAction :txt="t2('Cancel')" :submit="() => setState('enter password')" />
            </div>
          </template>
        </MyForm>
      </UiGrow>

      <UiGrow :open="data.state === 'forgot password confirm'" destroy-on-close>
        <AuthCode
          :save="(i) => forgotPassword2(i)"
          :notify="notify"
          :email="emailInput"
          class="_pl14 _pr14"
          :email-invalid="(i) => (data.emailInvalid = i)"
          :code-ref="data.forgotRef"
          :label="t2('Update password')"
          :cancel="() => setState('enter password')"
        />
      </UiGrow>

      <UiGrow :open="helpers.isIn(data.state, 'register form edit', 'register form')" :render-on-close="data.state === 'register confirm'">
        <MyForm :form="formRegiser" class="_hm50 _pr04 _pl04 _pt1 _pb1" :txt="data.state === 'register form edit' ? t2('Confirm email') : t2('Get email confirmation code')">
          <InputText :ref="formRegiser.refs.name" :label="t2('Name')" autocomplete="given-name" />
          <InputText :ref="formRegiser.refs.lastname" :label="t2('Last name')" autocomplete="family-name" />
          <InputPassword :ref="formRegiser.refs.password" type="password:register" />
          <InputAggree :ref="formRegiser.refs.newsletter" type="newsletter" optional />
          <AuthTerms class="_pl04 _pr04" place="register" />
          <template #button="{ submit, label }">
            <ButtonAction color :txt="label" :submit />
          </template>
        </MyForm>

        <UiWhy class="_pl1 _pb1">{{ t2('whyEmailCode') }}</UiWhy>
      </UiGrow>

      <UiGrow :open="data.state === 'register confirm'" destroy-on-close>
        <div :class="$style.boxRowPad">
          <UiLine :label="t2('Name')" :value="data.register?.register?.name ?? '...'" />
          <UiLine :label="t2('Last name')" :value="data.register?.register?.lastname ?? '...'" />
          <ButtonIcon size="36" :class="$style.iconMain" :txt="t2('Edit profile')" @click.passive="data.state = 'register form edit'">
            <IconEdit class="_svg18" />
          </ButtonIcon>
        </div>

        <AuthCode
          :save="(i) => registerConfirm(i)"
          :notify="notify"
          :code-ref="data.registerRef"
          :email="emailInput"
          :email-invalid="(i) => (data.emailInvalid = i)"
          class="_pl14 _pr14"
          :cancel="() => (data.state = 'register form edit')"
          :label="t2('Register')"
        />
      </UiGrow>
    </form>

    <UiGrow :open="data.state === 'verify email loading'" destroy-on-close>
      <AuthCode
        v-slot="{ getInputCode }"
        :save="(i) => sendCodeStep2(i)"
        :notify="notify"
        :email="emailInput"
        :email-invalid="(i) => (data.emailInvalid = i)"
        :code-ref="data.loginRef"
        :label="t2('Sign in')"
        class="_pl14 _pr14"
        :cancel="() => setState('enter password')"
      >
        <ButtonActionArrow color :txt="t2('Sign in')" :submit="() => sendCodeStep2(getInputCode())" />
      </AuthCode>
    </UiGrow>

    <UiGrow :open="data.state === 'is auth'">
      <div :class="$style.boxRowPad">
        <b>{{ helpers.joinString(' ', user?.name, user?.lastname) || '...' }}</b>
        <ButtonIcon :class="$style.iconMain" size="36" :to="myLink('user-profile')" :txt="t2('Manage account')">
          <IconSettings :class="$style.iconSettings" />
        </ButtonIcon>
      </div>
    </UiGrow>

    <UiGrow :open="!!user.userId && !user.isAuth && data.state === 'enter email'">
      <div class="_pt14 _row _gap04 _pl04 _pb04 _pr04 _wrap _gap04">
        <LazyAuthLoginGoogle :mutate :checkout-id="checkout?.transferCheckout" />
        <!-- <LazyAuthLoginApple :mutate /> -->
        <LazyAuthLoginFacebook :mutate />
        <AuthTerms class="_pt1 _pl04 _pr04 _pb04" place="social" />
      </div>
    </UiGrow>
  </div>
</template>

<style module>
.cont {
  composes: global(_relative);
  composes: global(_wm70ch);
  composes: global(_hm50);
  composes: global(_w100p);
  composes: global(_br14);
  composes: global(_hidden);
  composes: global(_b5);
  composes: global(_wmin240);
  composes: global(_bg0);
}

.cont:global(.pending) {
  border-color: var(--blue3);
  box-shadow: 0 0 0 3px hsla(240, 100%, 33.9%, 0.1);
}

.iconMain {
  composes: global(_absolute);
  composes: global(_row);
  composes: global(_ctrY);
  composes: global(_mta);
  composes: global(_mba);
  composes: global(_bg0);
  top: 0;
  z-index: 1;
  right: 6px;
  bottom: 0;
  composes: global(_c2);
}

.iconMain:hover {
  color: var(--black);
  border-color: var(--black-4);
}

.iconSettings {
  composes: global(_svg);
  --svg: 22px;
  --svg-color: var(--black);
}

.label {
  composes: global(_fw-xl);
  composes: global(_ls-xs);
  composes: global(_fs09);
  composes: global(_absolute);
  composes: global(_gap1);
  top: 3px;
  left: 14px;
  composes: global(_c2);
  composes: global(_row);
  composes: global(_ctrY);
}

.input {
  composes: global(_h100p);
  composes: global(_w100p);
  composes: global(d_fs1);
  composes: global(m_fs11);
  composes: global(_pt08);
  composes: global(_pl14);
  border: none;
  color: var(--blue2);
  background: transparent;
}

.input:placeholder-shown,
.input::placeholder {
  color: var(--black-4);
  opacity: 0.7;
}

.btnSucess {
  composes: global(_absolute);
  composes: global(_noselect);
  composes: global(_opacity0);
  visibility: hidden;
  height: 0;
  width: 0;
}

.boxPad {
  composes: global(_row);
  composes: global(_wrap);
  composes: global(_gap06);
  composes: global(_hm50);
  composes: global(_pt1);
  composes: global(_pb1);
  composes: global(_pl04);
  composes: global(_pr04);
  composes: global(_spaced);
}

.boxRight {
  composes: global(_row);
  composes: global(_wrap);
  composes: global(_gap04);
  flex: 1 0 100%;
}

.b1 {
  composes: global(_row);
  composes: global(_ctrY);
  composes: global(_fs09);
  composes: global(_gap3);
  composes: global(_relative);
  composes: global(m_h60);
  composes: global(d_h50);
  composes: global(_mb-1);
}

.boxRowPad {
  composes: b1;
  composes: global(_pl14);
  composes: global(_pr14);
}
</style>
